import { useEffect, useRef, useState } from 'react';
import type { FeedStatus, Opportunity } from './api.ts';

/**
 * The live WebSocket connection to the API.
 *
 * Reconnects with backoff, because the socket will drop — a laptop sleeping, a
 * redeploy, nginx recycling. The `connected` flag is surfaced to the UI rather
 * than hidden, so a stalled connection reads as "reconnecting" instead of
 * silently showing prices that stopped updating a minute ago.
 */

interface LiveState {
  opportunities: Opportunity[];
  feeds: FeedStatus[];
  connected: boolean;
}

type Message =
  | { type: 'opportunities'; data: Opportunity[] }
  | { type: 'feeds'; data: FeedStatus[] };

export function useLiveStream(enabled: boolean): LiveState {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [feeds, setFeeds] = useState<FeedStatus[]>([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let closed = false;
    let retryDelay = 1000;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (closed) return;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const socket = new WebSocket(`${protocol}//${window.location.host}/api/stream`);
      socketRef.current = socket;

      socket.onopen = () => {
        setConnected(true);
        retryDelay = 1000;
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data as string) as Message;
          if (message.type === 'opportunities') setOpportunities(message.data);
          else if (message.type === 'feeds') setFeeds(message.data);
        } catch {
          // A malformed frame is not worth tearing the connection down for.
        }
      };

      socket.onclose = () => {
        setConnected(false);
        if (closed) return;
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(15_000, retryDelay * 2);
      };

      socket.onerror = () => socket.close();
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled]);

  return { opportunities, feeds, connected };
}
